import { entra } from "./entra";

const apiBase =
  window.location.hostname === "localhost" ? "" : "https://api.earthboundmartian.com";

function apiUrl(path: string): string {
  return `${apiBase}${path}`;
}

export type Rig = "commodore" | "serenity";

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface Trip extends DateRange {
  id: string;
  rig: Rig;
  status: "hold" | "booked" | "blocked";
  origin: "native" | "import";
  source: "direct" | "outdoorsy" | "rvshare" | "rvezy" | "other";
  guestLabel?: string;
  notes?: string;
}

export interface Feed {
  id: string;
  rig: Rig;
  name: string;
  url: string;
  lastSyncAt?: string;
  lastError?: string;
  lastEventCount?: number;
}

export interface Quote extends DateRange {
  id: string;
  rig: Rig;
  name: string;
  email: string;
  phone?: string;
  destination?: string;
  guests?: number;
  notes?: string;
  status: "new" | "contacted" | "quoted" | "booked" | "closed";
  createdAt: string;
}

async function parseError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string };
    return body.error || response.statusText;
  } catch {
    return response.statusText;
  }
}

export async function fetchAvailability(rig: Rig): Promise<DateRange[]> {
  const response = await fetch(apiUrl(`/v1/availability?rig=${rig}`));
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  const body = (await response.json()) as { ranges: DateRange[] };
  return body.ranges ?? [];
}

export async function submitQuote(payload: {
  rig: Rig;
  name: string;
  email: string;
  phone?: string;
  startDate: string;
  endDate: string;
  destination?: string;
  guests?: number;
  notes?: string;
  company?: string;
}): Promise<void> {
  const response = await fetch(apiUrl("/v1/quotes"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
}

type TokenFn = () => Promise<string>;

async function opsFetch(path: string, token: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(apiUrl(path), { ...init, headers });
}

export function createOpsClient(getToken: TokenFn) {
  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = await getToken();
    const response = await opsFetch(path, token, init);
    if (response.status === 204) {
      return undefined as T;
    }
    if (!response.ok) {
      throw new Error(await parseError(response));
    }
    return (await response.json()) as T;
  }

  return {
    listTrips: (rig: Rig) => request<{ items: Trip[] }>(`/v1/ops/${rig}/trips`).then((b) => b.items),
    createTrip: (rig: Rig, body: Partial<Trip>) =>
      request<Trip>(`/v1/ops/${rig}/trips`, { method: "POST", body: JSON.stringify(body) }),
    updateTrip: (rig: Rig, id: string, body: Partial<Trip>) =>
      request<Trip>(`/v1/ops/${rig}/trips/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    deleteTrip: (rig: Rig, id: string) =>
      request<void>(`/v1/ops/${rig}/trips/${id}`, { method: "DELETE" }),
    listFeeds: (rig: Rig) => request<{ items: Feed[] }>(`/v1/ops/${rig}/feeds`).then((b) => b.items),
    createFeed: (rig: Rig, body: { name: string; url: string }) =>
      request<Feed>(`/v1/ops/${rig}/feeds`, { method: "POST", body: JSON.stringify(body) }),
    deleteFeed: (rig: Rig, id: string) =>
      request<void>(`/v1/ops/${rig}/feeds/${id}`, { method: "DELETE" }),
    syncFeed: (rig: Rig, id: string) =>
      request<Feed>(`/v1/ops/${rig}/feeds/${id}/sync`, { method: "POST" }),
    listQuotes: (rig: Rig) =>
      request<{ items: Quote[] }>(`/v1/ops/${rig}/quotes`).then((b) => b.items),
    updateQuote: (rig: Rig, id: string, body: Partial<Quote>) =>
      request<Quote>(`/v1/ops/${rig}/quotes/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    exportUrl: (rig: Rig) => apiUrl(`/v1/calendar/${rig}.ics`),
    scope: entra.scope,
  };
}
